import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Building2, Users, CreditCard, Edit, Trash2,
  Plus, Mail, Globe, Calendar, Shield, AlertCircle,
  CheckCircle, XCircle, Settings, User, Zap, BookOpen,
  Palette, Image, ExternalLink, Clock
} from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import "../../styles/UtopiaPages.css";

interface SchoolDetailsData {
  id: number;
  name: string;
  subdomain: string;
  billing_email: string;
  status: string;
  subscription_tier: string;
  is_subscription_exempt?: number;
  max_students: number;
  max_teachers: number;
  created_at: string;
  trial_ends_at: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  user_counts: {
    total_users: number;
    admin_count: number;
    teacher_count: number;
    student_count: number;
  };
  subscription: {
    id: number;
    plan_type: string;
    amount: number;
    currency: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
  } | null;
  admins: Array<{
    id: number;
    name: string;
    email: string;
    role: string;
    createdAt: string;
  }>;
  headteachers: Array<{
    id: number;
    name: string;
    email: string;
    role: string;
    createdAt: string;
  }>;
}

interface SchoolSubject {
  id: number;
  name: string;
  tenant_id: number | null;
  createdAt: string;
}

const SchoolDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";
  
  const [school, setSchool] = useState<SchoolDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [actionLoading, setActionLoading] = useState<string>("");
  
  // Subjects state
  const [subjects, setSubjects] = useState<SchoolSubject[]>([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  const fetchSchool = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl(`superadmin/schools/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchool(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load school details");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const fetchSchoolSubjects = useCallback(async () => {
    if (!id) return;
    try {
      setSubjectsLoading(true);
      const response = await axios.get(getApiUrl(`academics/subjects`), {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': id }
      });
      const subjectsData = Array.isArray(response.data)
        ? response.data
        : response.data.subjects || [];
      setSubjects(subjectsData);
    } catch (err: any) {
      console.error("Failed to fetch school subjects:", err);
    } finally {
      setSubjectsLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    if (id) {
      fetchSchool();
      fetchSchoolSubjects();
    }
  }, [id, fetchSchool, fetchSchoolSubjects]);

  const handleStatusChange = async (newStatus: string) => {
    if (!window.confirm(`Are you sure you want to ${newStatus} this school?`)) {
      return;
    }
    
    setActionLoading(newStatus);
    try {
      await axios.patch(
        getApiUrl(`superadmin/schools/${id}`),
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSchool();
    } catch (err: any) {
      alert(err.response?.data?.message || `Failed to ${newStatus} school`);
    } finally {
      setActionLoading("");
    }
  };

  const handleSubscriptionExemptToggle = async () => {
    const makeExempt = !school?.is_subscription_exempt;
    const confirmText = makeExempt
      ? "Remove this school from billing? It will use the system without subscription issues."
      : "Re-enable billing for this school?";
    if (!window.confirm(confirmText)) {
      return;
    }

    setActionLoading(makeExempt ? "exempt" : "billing");
    try {
      await axios.patch(
        getApiUrl(`superadmin/schools/${id}`),
        { is_subscription_exempt: makeExempt ? 1 : 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSchool();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to update billing status");
    } finally {
      setActionLoading("");
    }
  };

  const handleDeleteAdmin = async (adminId: number, adminName: string) => {
    if (!window.confirm(`Are you sure you want to delete admin "${adminName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(
        getApiUrl(`superadmin/schools/${id}/admins/${adminId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSchool();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to delete admin");
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { bg: string, text: string, icon: React.ReactNode }> = {
      active: { bg: "bg-teal-50", text: "text-teal-700", icon: <CheckCircle size={14} /> },
      trial: { bg: "bg-amber-50", text: "text-amber-700", icon: <Zap size={14} /> },
      pending: { bg: "bg-blue-50", text: "text-blue-700", icon: <Clock size={14} /> },
      suspended: { bg: "bg-rose-50", text: "text-rose-700", icon: <XCircle size={14} /> },
      expired: { bg: "bg-slate-50", text: "text-slate-700", icon: <AlertCircle size={14} /> }
    };

    const config = configs[status] || configs.expired;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${config.bg} ${config.text}`}>
        {config.icon} {status}
      </span>
    );
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: <Clock size={14} />, color: 'blue' },
    { value: 'trial', label: 'Trial', icon: <Zap size={14} />, color: 'amber' },
    { value: 'active', label: 'Active', icon: <CheckCircle size={14} />, color: 'teal' },
    { value: 'suspended', label: 'Suspended', icon: <XCircle size={14} />, color: 'rose' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-vh-100 bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.2em]">Accessing School Records...</p>
        </div>
      </div>
    );
  }

  if (error || !school) {
    return (
      <div className="dashboard-container">
        <main className="dashboard-main p-8 bg-slate-50/30">
          <div className="max-w-2xl mx-auto text-center py-20">
            <div className="w-20 h-20 rounded-[2.5rem] bg-rose-50 flex items-center justify-center mx-auto mb-6">
              <Shield size={32} className="text-rose-500" />
            </div>
            <h2 className="text-2xl font-black text-slate-800 mb-3">Access Restricted</h2>
            <p className="text-rose-600/70 font-medium">{error || "The requested school instance could not be found or access is restricted."}</p>
            <button onClick={() => navigate("/superadmin/schools")} className="mt-8 bg-slate-900 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all">
              Return to Registry
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/30">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate("/superadmin/schools")}
              className="w-12 h-12 flex items-center justify-center bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-teal-600 hover:shadow-xl hover:shadow-teal-500/5 transition-all"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-4">
              {/* School Logo */}
              {school.logo_url ? (
                <img 
                  src={school.logo_url} 
                  alt={`${school.name} Logo`}
                  className="w-14 h-14 rounded-xl object-contain bg-white border border-slate-200 shadow"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-teal-500 flex items-center justify-center shadow-lg shadow-teal-500/20">
                  <Building2 size={24} className="text-white" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[10px] font-black text-teal-600 uppercase tracking-[0.2em] bg-teal-50 px-3 py-1 rounded-full">Instance Details</span>
                </div>
                <h1 className="text-3xl font-black text-slate-800 tracking-tight">{school.name}</h1>
                <div className="flex items-center gap-3 mt-2">
                  <Globe size={14} className="text-slate-400" />
                  <span className="text-sm font-bold text-slate-500 lowercase">{school.subdomain}.msportal.com</span>
                  {getStatusBadge(school.status)}
                  {school.is_subscription_exempt ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-slate-900 text-white">
                      Billing Exempt
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {/* Status Quick Change Buttons */}
            <div className="flex flex-wrap gap-2">
              {statusOptions.filter(opt => opt.value !== school.status).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleStatusChange(opt.value)}
                  disabled={!!actionLoading}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs uppercase tracking-wider transition-all disabled:opacity-50 ${
                    opt.color === 'blue' ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-100' :
                    opt.color === 'amber' ? 'bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-100' :
                    opt.color === 'teal' ? 'bg-teal-50 text-teal-600 hover:bg-teal-100 border border-teal-100' :
                    'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100'
                  }`}
                >
                  {opt.icon} {opt.label}
                </button>
              ))}
              <button
                onClick={handleSubscriptionExemptToggle}
                disabled={!!actionLoading}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs uppercase tracking-wider transition-all disabled:opacity-50 ${
                  school.is_subscription_exempt
                    ? 'bg-slate-900 text-white hover:bg-slate-800 border border-slate-900'
                    : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100'
                }`}
              >
                {school.is_subscription_exempt ? 'Reinstate Billing' : 'Subscription Exempted'}
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/superadmin/schools/${id}/edit`)}
                className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10"
              >
                <Edit size={16} /> Reconfigure
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50">
            <div className="flex items-center gap-3 mb-3">
              <Users className="text-teal-500" size={20} />
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Users</span>
            </div>
            <p className="text-3xl font-black text-slate-800">{school.user_counts?.total_users || 0}</p>
            <div className="flex gap-4 mt-3 text-xs">
              <span className="text-slate-500">{school.user_counts?.admin_count || 0} Admins</span>
              <span className="text-slate-500">{school.user_counts?.teacher_count || 0} Teachers</span>
              <span className="text-slate-500">{school.user_counts?.student_count || 0} Students</span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50">
            <div className="flex items-center gap-3 mb-3">
              <CreditCard className="text-blue-500" size={20} />
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Subscription</span>
            </div>
            <p className="text-2xl font-black text-slate-800 capitalize">{school.subscription_tier || 'Basic'}</p>
            <p className="text-sm text-slate-500 mt-1">
              {school.subscription ? `${school.subscription.currency} ${school.subscription.amount}/${school.subscription.plan_type}` : 'No active subscription'}
            </p>
          </div>

          <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="text-purple-500" size={20} />
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Created</span>
            </div>
            <p className="text-xl font-black text-slate-800">{new Date(school.created_at).toLocaleDateString()}</p>
            <p className="text-sm text-slate-500 mt-1">Platform instance launch</p>
          </div>

          <div className={`p-6 rounded-[2rem] border shadow-xl ${
            school.status === 'active' ? 'bg-teal-50 border-teal-100' :
            school.status === 'trial' ? 'bg-amber-50 border-amber-100' :
            school.status === 'suspended' ? 'bg-rose-50 border-rose-100' :
            'bg-slate-50 border-slate-100'
          }`}>
            <div className="flex items-center gap-3 mb-3">
              <Shield className={school.status === 'active' ? 'text-teal-600' : school.status === 'trial' ? 'text-amber-600' : school.status === 'suspended' ? 'text-rose-600' : 'text-slate-600'} size={20} />
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</span>
            </div>
            <p className={`text-xl font-black uppercase tracking-wide ${
              school.status === 'active' ? 'text-teal-700' :
              school.status === 'trial' ? 'text-amber-700' :
              school.status === 'suspended' ? 'text-rose-700' :
              'text-slate-700'
            }`}>{school.status}</p>
            {school.is_subscription_exempt ? (
              <p className="text-xs font-bold text-slate-700 mt-2">Billing exemption is enabled for this school.</p>
            ) : null}
            {school.trial_ends_at && school.status === 'trial' && (
              <p className="text-xs font-bold text-amber-600 mt-2">
                Trial ends: {new Date(school.trial_ends_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Resource Quotas */}
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/30 p-10">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <Settings className="text-slate-400" size={20} />
                  <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Resource Quotas</h2>
                </div>
                <span className="text-[10px] font-black text-teal-600 uppercase tracking-widest bg-teal-50 px-3 py-1 rounded-full">
                  {school.subscription_tier || 'Basic'} Tier
                </span>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="bg-slate-50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-slate-600 uppercase tracking-wide">Max Students</span>
                    <Users size={16} className="text-slate-400" />
                  </div>
                  <p className="text-3xl font-black text-slate-800">{school.max_students}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-slate-600 uppercase tracking-wide">Max Teachers</span>
                    <User size={16} className="text-slate-400" />
                  </div>
                  <p className="text-3xl font-black text-slate-800">{school.max_teachers}</p>
                </div>
              </div>
            </div>

            {/* Subscription Details */}
            {school.subscription && (
              <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/30 p-10">
                <div className="flex items-center gap-3 mb-8">
                  <CreditCard className="text-blue-500" size={20} />
                  <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Subscription Details</h2>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-slate-50 rounded-2xl p-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Plan Type</span>
                    <p className="text-xl font-black text-slate-800 capitalize mt-1">{school.subscription.plan_type}</p>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Amount</span>
                    <p className="text-xl font-black text-slate-800 mt-1">
                      {school.subscription.currency} {school.subscription.amount}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Period Start</span>
                    <p className="text-lg font-bold text-slate-800 mt-1">
                      {new Date(school.subscription.current_period_start).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Period End</span>
                    <p className="text-lg font-bold text-slate-800 mt-1">
                      {new Date(school.subscription.current_period_end).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Branding - Logo & Colors */}
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/30 p-10">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <Palette className="text-purple-500" size={20} />
                  <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Branding & Logo</h2>
                </div>
                <button
                  onClick={() => navigate(`/superadmin/schools/${id}/edit`)}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-slate-200 transition-all"
                >
                  <Edit size={14} /> Edit Branding
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Logo */}
                <div className="bg-slate-50 rounded-2xl p-6">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">School Logo</span>
                  {school.logo_url ? (
                    <div className="flex items-center gap-4">
                      <img 
                        src={school.logo_url} 
                        alt="School Logo" 
                        className="w-20 h-20 object-contain rounded-xl bg-white border border-slate-200"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <div>
                        <p className="text-sm font-bold text-slate-700">Logo configured</p>
                        <p className="text-xs text-slate-400 mt-1">Click Edit to change</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <Image size={40} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-sm text-slate-400">No logo configured</p>
                    </div>
                  )}
                </div>

                {/* Colors */}
                <div className="bg-slate-50 rounded-2xl p-6">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">Brand Colors</span>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-8 h-8 rounded-lg border border-slate-200" 
                          style={{ backgroundColor: school.primary_color || '#0d9488' }}
                        />
                        <span className="text-sm font-bold text-slate-600">Primary</span>
                      </div>
                      <p className="text-xs text-slate-400 font-mono">{school.primary_color || '#0d9488'}</p>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-8 h-8 rounded-lg border border-slate-200" 
                          style={{ backgroundColor: school.secondary_color || '#64748b' }}
                        />
                        <span className="text-sm font-bold text-slate-600">Secondary</span>
                      </div>
                      <p className="text-xs text-slate-400 font-mono">{school.secondary_color || '#64748b'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Color Preview */}
              {(school.primary_color || school.secondary_color) && (
                <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: `${school.primary_color || '#0d9488'}15` }}>
                  <p className="text-sm font-bold mb-2" style={{ color: school.primary_color || '#0d9488' }}>Primary Color Preview</p>
                  <p className="text-sm" style={{ color: school.secondary_color || '#64748b' }}>Secondary Color Preview</p>
                </div>
              )}
            </div>

            {/* School Subjects */}
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/30 p-10">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <BookOpen className="text-indigo-500" size={20} />
                  <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Imported Subjects</h2>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchSchoolSubjects()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-100 transition-all"
                  >
                    <BookOpen size={14} /> Refresh Catalog
                  </button>
                  <button
                    onClick={() => navigate("/superadmin/subjects")}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-indigo-600 transition-all"
                  >
                    <ExternalLink size={14} /> Manage Global Subjects
                  </button>
                </div>
              </div>

              <div className="mb-6 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4">
                <p className="text-sm font-semibold text-indigo-800">
                  This school imports subjects from the global catalog.
                </p>
                <p className="mt-1 text-sm text-indigo-700/80">
                  Only imported subjects appear here. Schools choose their own imports from the tenant subject management page, then assign those subjects to classes.
                </p>
              </div>

              {subjectsLoading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mx-auto"></div>
                </div>
              ) : subjects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {subjects.map((subject) => (
                    <div key={subject.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                          <BookOpen size={18} className="text-indigo-600" />
                        </div>
                        <div>
                          <span className="font-bold text-slate-800 block">{subject.name}</span>
                          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Imported For This School</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <BookOpen size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No subjects have been imported for this school yet</p>
                  <button
                    onClick={() => navigate("/superadmin/subjects")}
                    className="mt-3 text-indigo-500 font-bold text-sm hover:underline"
                  >
                    Open global subject catalog
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/30 p-8">
              <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight mb-6">Quick Actions</h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/superadmin/schools/${id}/edit`)}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 rounded-2xl hover:bg-teal-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center group-hover:bg-teal-500 transition-all">
                    <Edit size={18} className="text-teal-600 group-hover:text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-800 text-sm">Edit School</p>
                    <p className="text-xs text-slate-400">Update details & branding</p>
                  </div>
                </button>

                <button
                  onClick={() => navigate(`/superadmin/schools/${id}/admins/new`)}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 rounded-2xl hover:bg-blue-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-500 transition-all">
                    <User size={18} className="text-blue-600 group-hover:text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-800 text-sm">Add Admin</p>
                    <p className="text-xs text-slate-400">Create new admin account</p>
                  </div>
                </button>

                <button
                  onClick={() => navigate(`/superadmin/subscriptions?school=${id}`)}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 rounded-2xl hover:bg-purple-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center group-hover:bg-purple-500 transition-all">
                    <CreditCard size={18} className="text-purple-600 group-hover:text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-800 text-sm">Manage Subscription</p>
                    <p className="text-xs text-slate-400">View or update billing</p>
                  </div>
                </button>

                <button
                  onClick={() => window.open(`https://${school.subdomain}.msportal.com`, '_blank')}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 rounded-2xl hover:bg-indigo-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-500 transition-all">
                    <ExternalLink size={18} className="text-indigo-600 group-hover:text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-800 text-sm">Visit School Site</p>
                    <p className="text-xs text-slate-400">Open school portal</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/30 p-8">
              <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl">
                  <Mail size={18} className="text-slate-400" />
                  <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Billing Email</span>
                    <span className="text-sm font-bold text-slate-700">{school.billing_email}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Admins */}
            <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl shadow-slate-900/30">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-black uppercase tracking-tight">Instance <span className="text-teal-400">Admins</span></h3>
                <button
                  onClick={() => navigate(`/superadmin/schools/${id}/admins/new`)}
                  className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                >
                  <Plus size={18} />
                </button>
              </div>

              <div className="space-y-3">
                {school.admins && school.admins.length > 0 ? (
                  school.admins.map((admin) => (
                    <div key={admin.id} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center text-teal-400 font-bold">
                          {admin.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold text-sm">{admin.name}</p>
                          <p className="text-xs text-slate-400">{admin.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                        className="p-2 rounded-xl bg-white/5 text-slate-400 hover:bg-rose-500/20 hover:text-rose-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <User size={32} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">No admins assigned</p>
                  </div>
                )}
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-rose-50 rounded-[2.5rem] p-8 border border-rose-100">
              <h3 className="text-lg font-black text-rose-800 uppercase tracking-tight mb-4">Danger Zone</h3>
              <p className="text-sm text-rose-600/70 font-medium mb-4">
                Once you suspend a school, all users in that school will lose access to the system.
              </p>
              <button
                onClick={() => handleStatusChange('suspended')}
                disabled={school.status === 'suspended' || !!actionLoading}
                className="w-full flex items-center justify-center gap-2 bg-rose-500 text-white px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={16} />
                {school.status === 'suspended' ? 'Already Suspended' : 'Suspend Instance'}
              </button>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
};

export default SchoolDetails;












